import {
  TellescopeModelFromName,
  useAuth,
  useEnduserTimelineItems,
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { useCallingShallowStore } from "../../store/calling-store";
import {
  ExpandableDialogWithSidebar,
  type ExpandableDialogWithSidebarProps,
  LoadingScreen,
  useStateWithSetAction,
  withKey,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useMemo, useRef, useState } from "react";
import {
  AddBPReadingSubsection,
  AddOtherReadingSubsection,
  AddRelatedContactForm,
  ChargePaymentForm,
  ContentSection,
  EligibilitySection,
  EligibilitySubmissionForm,
  EncountersSection,
  EventsSection,
  FilesSection,
  FormResponse,
  FormResponses,
  FormsSection,
  InformationSection,
  LogPurchaseForm,
  MedicationsSection,
  MedicationUploadForm,
  OrdersSection,
  PaymentSection,
  RelatedContactsSection,
  ShareContentForm,
  TicketsSection,
  UploadFileForm,
  VitalsProgramSubsection,
} from "./sections";
import {
  CalendarCheck,
  ClipboardCheck,
  Clock,
  Package,
  RefreshCw,
  Ticket,
  UserRound,
  Users,
  File,
  FilePlus2,
  FileText,
  FileImage,
  Pill,
  CloudUpload,
  DollarSign,
  BanknoteArrowDown,
  CreditCard,
  Plus,
  ExternalLink,
  Phone,
  MessageSquare,
  HeartPulse,
  Sparkles,
} from "lucide-react";
import { formatDateTime, formatName, ObservationType } from "@tellescope/utils";
import { TimelineSection } from "./sections/TimelineSection/TimelineSection";
import { useShallowSidebarStore } from "../../store/sidebar-store";
import { convertStringToContactDetailsSection } from "../SidebarPanel/ContactDetailsSidebarContent";
import { useNavigate } from "react-router-dom";
import { TimelineSettingsRow } from "./sections/TimelineSection/TimelineSettingsRow";
import { VitalsSection } from "./sections/VitalsSection/VitalsSection";
import { toast } from "sonner";

/**
 * Props for the ContactsDialog component.
 *
 * @extends {ExpandableDialogWithSidebarProps} - Inherits all props from ExpandableDialogWithSidebar except title, setSelectedSection, and selectedSection
 *
 * @property {string} contactId - The unique identifier of the contact (enduser) to display
 * @property {string} [initialSection] - Optional initial section to display when the dialog opens. Defaults to "Information" if not provided
 */
export interface ContactsDialogProps
  extends Omit<
    ExpandableDialogWithSidebarProps,
    "title" | "setSelectedSection" | "selectedSection" | "children" | "headerActions"
  > {
  contactId: string;
  initialSection?: string;
}

/**
 * ContactsDialog - A comprehensive dialog component for displaying and managing contact (enduser) information.
 *
 * This component provides a multi-section interface with expandable sidebar navigation for viewing and
 * managing various aspects of a contact's data, including personal information, timeline events, tickets,
 * forms, payments, medications, and more.
 *
 * Features:
 * - Loads contact data and eligibility records via API queries
 * - Section-based navigation with deep linking support via initialSection prop
 * - Conditional actions based on contact data (e.g., insurance-based eligibility checks, Stripe payments)
 * - Form state management with automatic cleanup when navigating away from Forms sections
 * - Integration with ExpandableDialogWithSidebar for consistent UI patterns
 *
 * @param props - Component props
 * @returns The rendered ContactsDialog component
 *
 * @example
 * ```tsx
 * <ContactsDialog
 *   contactId="contact_123"
 *   initialSection="Timeline"
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * />
 * ```
 */
export const ContactsDialog = withKey(({ contactId, initialSection, ...dialogProps }: ContactsDialogProps) => {
  const { selectedOrganization } = useAuth();
  const isEditingNoteRef = useRef(false);
  const [vitalsSubtypeMetadata, setVitalsSubtypeMetadata] = useState<{
    title: string;
    type: ObservationType;
    observationId: string | undefined;
  }>();
  const [selectedSection, _setSelectedSection] = useStateWithSetAction(initialSection ?? "Information", (newVal) => {
    if (vitalsSubtypeMetadata && !newVal.includes("vitals")) {
      setVitalsSubtypeMetadata(undefined);
    }
  });
  const [selectedForm, setSelectedForm] = useState<{ formId: string; formTitle: string }>();
  const [selectedFormResponse, setSelectedFormResponse] = useState<TellescopeModelFromName<"form_responses">>();
  const { data: contact, isLoading } = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: contactId,
    enabled: !!contactId,
  });
  const { data: eligibilityRecords } = useTellescopeInfiniteQuery({
    model: "enduser_eligibility_results",
    method: "getSome",
    request: {
      filter: {
        enduserId: contactId,
      },
    },
    select: (data) => data.pages.flat(),
  });
  const { openSidebar } = useShallowSidebarStore("openSidebar");
  const { openDialer } = useCallingShallowStore("openDialer");
  const { session } = useAuth();
  const navigate = useNavigate();
  const hasTwilioAccess = !!(session?.userInfo?.twilioNumber || session?.userInfo?.orgTwilioNumber);

  /**
   * Memoized contact full name formatted as "FirstName LastName".
   * Gracefully handles missing first or last names by trimming whitespace.
   * Returns empty string if both names are undefined.
   */
  const contactName = useMemo(() => (contact ? formatName(contact) : ""), [contact]);
  const { t } = useTranslation();

  /**
   * Handles section navigation with automatic form state cleanup.
   *
   * This function manages the selectedSection state and ensures that form-related state
   * (selectedForm and selectedFormResponse) is cleared when navigating away from the Forms section.
   * This prevents stale form data from persisting when users navigate to other sections.
   *
   * Form state is preserved when navigating within Forms subsections (e.g., Forms -> Forms.FormResponses).
   *
   * @param val - The new section key or a function that returns the new section key
   */
  const handleSectionNavigation: Dispatch<SetStateAction<string>> = (val) => {
    _setSelectedSection((prev) => {
      const newVal = typeof val === "function" ? val(prev) : val;
      if (!newVal.startsWith("Forms")) {
        setSelectedForm(undefined);
        setSelectedFormResponse(undefined);
      }
      return newVal;
    });
  };

  const { i18n } = useTranslation();
  const { summaryData } = useEnduserTimelineItems({
    enduserId: contactId,
    queryOptions: {
      enabled: selectedSection === "Timeline",
    },
    locale: i18n.language,
    onOutOfCredits: () => {
      toast.error(t("You have run out of AI credits. Please purchase more to generate a summary for this timeline."));
    },
  });

  return (
    <>
      <ExpandableDialogWithSidebar
        {...dialogProps}
        expanded
        hideExpandToggle
        modal={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (isEditingNoteRef.current) e.preventDefault();
        }}
        title={contactName}
        selectedSection={selectedSection}
        setSelectedSection={handleSectionNavigation}
        headerActions={[
          ...(hasTwilioAccess && contact?.phone
            ? [
                {
                  hoverLabel: t("Call"),
                  icon: Phone,
                  onClick: () => openDialer({ enduser: contact }),
                },
              ]
            : []),
          {
            hoverLabel: t("Compose message"),
            icon: MessageSquare,
            onClick: () => {
              const params = new URLSearchParams({ "composeTo.enduserId": contactId });
              if (contact?.phone) params.set("composeTo.number", contact.phone);
              navigate(`/messaging/inbox/sms/new?${params.toString()}`);
            },
          },
          {
            hoverLabel: t("Open in sidebar"),
            icon: ExternalLink,
            onClick: () => {
              const convertedSection = convertStringToContactDetailsSection(selectedSection);
              if (!convertedSection) return;
              openSidebar("ContactDetails", {
                enduserId: contactId,
                selectedSection: convertedSection,
              });
              dialogProps.setOpen?.(false);
            },
          },
        ]}
      >
        {contact ? (
          <>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Information"
              title={t("Information")}
              icon={UserRound}
              className="overflow-x-hidden"
            >
              <InformationSection
                contact={contact}
                onNoteEditingChange={(isEditing) => (isEditingNoteRef.current = isEditing)}
              />
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Related Contacts"
              title={t("Related Contacts")}
              icon={Users}
              trailingText={t("({{amount}} total)", { amount: contact.relationships?.length ?? 0 })}
              actions={[
                {
                  icon: Plus,
                  onClick: () => {
                    handleSectionNavigation("Related Contacts.AddRelatedContact");
                  },
                },
              ]}
            >
              <RelatedContactsSection contact={contact} />
              <ExpandableDialogWithSidebar.Subsection sectionKey="AddRelatedContact" title={t("Add Related Contact")}>
                <AddRelatedContactForm
                  contact={contact}
                  onSuccess={() => handleSectionNavigation("Related Contacts")}
                />
              </ExpandableDialogWithSidebar.Subsection>
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              key="Timeline"
              title={t("Timeline")}
              icon={Clock}
              actions={
                summaryData.summary || !selectedOrganization?.bedrockAIAllowed
                  ? []
                  : [
                      {
                        label: summaryData.isOutOfCredits
                          ? t("Cannot Generate AI Summary (out of credits)")
                          : summaryData.canGenerateNewSummary
                          ? t("Generate AI Summary")
                          : t("Generating..."),
                        icon: Sparkles,
                        onClick: summaryData.generateSummary,
                        disabled:
                          !summaryData.canGenerateNewSummary || summaryData.isLoading || summaryData.isGenerating,
                      },
                    ]
              }
            >
              <TimelineSettingsRow className="pb-4 pt-1" />
              <TimelineSection
                contact={contact}
                summary={summaryData.summary}
                summaryIsGenerating={summaryData.isGenerating || summaryData.isLoading}
              />
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section key="Tickets" title={t("Tickets")} icon={Ticket}>
              <TicketsSection contact={contact} />
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section key="Events" title={t("Events")} icon={CalendarCheck}>
              <EventsSection contact={contact} />
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Eligibility"
              title={t("Eligibility")}
              icon={ClipboardCheck}
              actions={
                contact.insurance || contact.insuranceSecondary
                  ? [
                      {
                        icon: ClipboardCheck,
                        onClick: () => {
                          handleSectionNavigation("Eligibility.EligibilitySubmissionForm");
                        },
                      },
                    ]
                  : []
              }
              trailingText={`(${eligibilityRecords?.length ?? 0} total)`}
            >
              <EligibilitySection contact={contact} />
              <ExpandableDialogWithSidebar.Subsection
                sectionKey="EligibilitySubmissionForm"
                title={t("Submit Eligibility Check")}
              >
                <EligibilitySubmissionForm
                  contact={contact}
                  onSubmit={() => {
                    handleSectionNavigation("Eligibility");
                  }}
                  onCancel={() => {
                    handleSectionNavigation("Eligibility");
                  }}
                />
              </ExpandableDialogWithSidebar.Subsection>
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section sectionKey="Orders" title={t("Orders")} icon={Package}>
              <OrdersSection contact={contact} />
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section sectionKey="Encounters" title={t("Encounters")} icon={RefreshCw}>
              <EncountersSection contact={contact} />
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Files"
              title={t("Files")}
              icon={File}
              actions={[
                {
                  icon: FilePlus2,
                  onClick: () => {
                    handleSectionNavigation("Files.UploadFile");
                  },
                },
              ]}
            >
              <FilesSection contact={contact} />
              <ExpandableDialogWithSidebar.Subsection sectionKey="UploadFile" title={t("Upload File")}>
                <UploadFileForm contactId={contact.id} onUpload={() => handleSectionNavigation("Files")} />
              </ExpandableDialogWithSidebar.Subsection>
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section sectionKey="Forms" title={t("Forms")} icon={FileText}>
              <FormsSection
                contact={contact}
                onSelectForm={(form) => {
                  setSelectedForm({
                    formId: form.formId,
                    formTitle: form.formTitle,
                  });
                  handleSectionNavigation("Forms.FormResponses");
                }}
              />
              {!!selectedForm && (
                <ExpandableDialogWithSidebar.Subsection
                  sectionKey="FormResponses"
                  title={selectedForm?.formTitle ?? t("Unknown Form")}
                  scrollable={false}
                >
                  <FormResponses
                    contactId={contact.id}
                    formId={selectedForm?.formId}
                    onResponseSelect={(response) => {
                      // TODO: handle response selection properly
                      setSelectedFormResponse(response);
                      handleSectionNavigation(`Forms.FormResponses.Response`);
                    }}
                  />
                  <ExpandableDialogWithSidebar.Subsection
                    sectionKey="Response"
                    title={
                      selectedFormResponse?.submittedAt
                        ? formatDateTime(selectedFormResponse.submittedAt, "MM/DD/YYYY at hh:mm:ss a")
                        : t("Response")
                    }
                  >
                    {!!selectedFormResponse?.submittedAt && (
                      <FormResponse
                        timestamp={selectedFormResponse?.submittedAt || selectedFormResponse?.updatedAt}
                        response={selectedFormResponse}
                        enduserId={contact.id}
                        enduserName={contactName}
                        onGeneratePDFClick={() => {
                          // TODO: Implement PDF generation for standalone form response view
                          console.log("Generate PDF clicked");
                        }}
                      />
                    )}
                  </ExpandableDialogWithSidebar.Subsection>
                </ExpandableDialogWithSidebar.Subsection>
              )}
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Content"
              title={t("Content")}
              icon={FileImage}
              actions={[
                {
                  icon: Plus,
                  onClick: () => {
                    handleSectionNavigation("Content.ShareContent");
                  },
                },
              ]}
            >
              <ContentSection contact={contact} />
              <ExpandableDialogWithSidebar.Subsection sectionKey="ShareContent" title={t("Share Content")}>
                <ShareContentForm contactId={contact.id} onSubmit={() => handleSectionNavigation("Content")} />
              </ExpandableDialogWithSidebar.Subsection>
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Medications"
              title={t("Medications")}
              icon={Pill}
              actions={[
                {
                  icon: CloudUpload,
                  onClick: () => handleSectionNavigation("Medications.UploadMedications"),
                },
              ]}
            >
              <MedicationsSection contact={contact} />
              <ExpandableDialogWithSidebar.Subsection
                sectionKey="UploadMedications"
                title={t("contacts.section.uploadMedications")}
              >
                <MedicationUploadForm contact={contact} onUpload={() => handleSectionNavigation("Medications")} />
              </ExpandableDialogWithSidebar.Subsection>
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Payment"
              title={t("Payment")}
              icon={DollarSign}
              actions={[
                {
                  icon: BanknoteArrowDown,
                  onClick: () => handleSectionNavigation("Payment.LogPurchase"),
                  label: t("Log Purchase"),
                },
                ...(contact.stripeCustomerId
                  ? [
                      {
                        icon: CreditCard,
                        onClick: () => handleSectionNavigation("Payment.Charge"),
                        label: t("Charge Payment"),
                      },
                    ]
                  : []),
              ]}
            >
              <PaymentSection contact={contact} />
              <ExpandableDialogWithSidebar.Subsection sectionKey="LogPurchase" title={t("Log Purchase")}>
                <LogPurchaseForm
                  contactId={contact.id}
                  onSubmit={() => {
                    handleSectionNavigation("Payment");
                  }}
                  onCancel={() => {
                    handleSectionNavigation("Payment");
                  }}
                />
              </ExpandableDialogWithSidebar.Subsection>
              {!!contact.stripeCustomerId && (
                <ExpandableDialogWithSidebar.Subsection sectionKey="Charge" title={t("Charge Payment")}>
                  <ChargePaymentForm
                    contactId={contact.id}
                    onSubmit={() => {
                      handleSectionNavigation("Payment");
                    }}
                    onCancel={() => {
                      handleSectionNavigation("Payment");
                    }}
                  />
                </ExpandableDialogWithSidebar.Subsection>
              )}
            </ExpandableDialogWithSidebar.Section>
            <ExpandableDialogWithSidebar.Section
              sectionKey="Vitals"
              title={t("Vitals")}
              icon={HeartPulse}
              scrollable={false}
            >
              <VitalsSection
                enduserId={contact.id}
                onItemClick={(observationId, type, title) => {
                  handleSectionNavigation("Vitals.vitals-subtype");
                  setVitalsSubtypeMetadata({ observationId, type, title });
                }}
                onAddReading={(type) => {
                  handleSectionNavigation(`Vitals.add-${type}-reading`);
                }}
              />
              <ExpandableDialogWithSidebar.Subsection
                sectionKey="vitals-subtype"
                title={vitalsSubtypeMetadata?.title ?? t("Unknown Type")}
                className="px-0"
              >
                {vitalsSubtypeMetadata?.type && (
                  <VitalsProgramSubsection
                    enduserId={contact.id}
                    observationType={vitalsSubtypeMetadata.type}
                    observationId={vitalsSubtypeMetadata.observationId}
                    setObservationId={(newVal) => {
                      setVitalsSubtypeMetadata((prev) => {
                        const newId = typeof newVal === "function" ? newVal(prev?.observationId) : newVal;
                        if (!prev) return prev;

                        return {
                          ...prev,
                          observationId: newId,
                        };
                      });
                    }}
                    unit={vitalsSubtypeMetadata.title}
                  />
                )}
              </ExpandableDialogWithSidebar.Subsection>
              <ExpandableDialogWithSidebar.Subsection
                sectionKey="add-bp-reading"
                title={t("Add BP Reading", { ns: "vitals" })}
              >
                <AddBPReadingSubsection
                  onSubmit={() => handleSectionNavigation("Vitals")}
                  onCancel={() => handleSectionNavigation("Vitals")}
                  enduserId={contact.id}
                />
              </ExpandableDialogWithSidebar.Subsection>
              <ExpandableDialogWithSidebar.Subsection
                sectionKey="add-other-reading"
                title={t("Add Other Reading", { ns: "vitals" })}
              >
                <AddOtherReadingSubsection
                  onSubmit={() => handleSectionNavigation("Vitals")}
                  onCancel={() => handleSectionNavigation("Vitals")}
                  enduserId={contact.id}
                />
              </ExpandableDialogWithSidebar.Subsection>
            </ExpandableDialogWithSidebar.Section>
          </>
        ) : isLoading ? (
          <div>
            <LoadingScreen />
          </div>
        ) : (
          <div color="red" data-testid="error-text">
            {t("Error loading contact")}
          </div>
        )}
      </ExpandableDialogWithSidebar>
    </>
  );
}, "contactId");
